import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useOpenOnNew from '../hooks/useOpenOnNew';
import { useAuth } from '../context/AuthContext';
import {
  getAccounts,
  getStages,
  getLeadSources,
  getUsers,
  createAccount,
  updateAccount,
  bulkUpdateAccounts,
  updateAccountGoal,
  bulkDeleteAccounts,
  bulkSetPrimaryOwner,
  bulkAddOwner,
  importAccounts,
  checkDuplicateAccount,
  getOwnershipRoleTypes,
  exportAccountsExcel,
  exportAccountsPdf,
} from '../api';
import Modal from '../components/Modal';
import KeyStar from '../components/KeyStar';
import ConfirmDialog from '../components/ConfirmDialog';
import SortableHeader from '../components/SortableHeader';
import ExportMenu from '../components/ExportMenu';
import CsvImportModal from '../components/CsvImportModal';
import Pagination from '../components/Pagination';
import { ColumnsButton } from '../components/ColumnConfigurator';
import SavedViews from '../components/SavedViews';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { TableSkeleton, CardSkeleton } from '../components/TableSkeleton';
import RowActions from '../components/RowActions';
import EmptyValue from '../components/EmptyValue';
import TypeaheadSelect from '../components/TypeaheadSelect';
import MobileCard from '../components/MobileCard';
import useColumns from '../hooks/useColumns';
import usePagination from '../hooks/usePagination';
import useMediaQuery from '../hooks/useMediaQuery';
import {
  Plus,
  Search,
  MoreHorizontal,
  Building2,
  ChevronDown,
  Trash2,
  Edit,
  Save,
  X,
  Upload,
  Eye,
} from 'lucide-react';
import EmptyState, { EmptyStateFiltered } from '../components/EmptyState';
import './Accounts.css';

function Accounts() {
  const { canDo } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { visibleColumns } = useColumns('accounts');
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Lookup data (not paginated)
  const [stages, setStages] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [ownershipRoleTypes, setOwnershipRoleTypes] = useState([]);
  const [lookupError, setLookupError] = useState('');

  // Filters - initialize from URL params
  const [stageFilter, setStageFilter] = useState(() => {
    const param = searchParams.get('stage_id');
    return param ? param.split(',') : [];
  });
  const [ownerFilter, setOwnerFilter] = useState([]);

  // Build extra params for the pagination hook
  const extraParams = useMemo(() => {
    const p = {};
    if (stageFilter.length) p.stage_id = stageFilter.join(',');
    if (ownerFilter.length) p.owner_id = ownerFilter.join(',');
    return p;
  }, [stageFilter, ownerFilter]);

  // Server-side pagination via hook
  const {
    data: accounts,
    loading,
    error: paginationError,
    page,
    setPage,
    limit,
    setLimit,
    search,
    setSearch,
    sortBy: sortField,
    sortOrder: sortDirection,
    handleSort,
    total,
    totalPages,
    refetch,
  } = usePagination(getAccounts, {
    queryKey: 'accounts',
    initialLimit: 25,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    debounceMs: 300,
    extraParams,
  });

  const error = lookupError || paginationError;

  // The star saves straight away and the page re-reads; a first click on
  // the Key header puts starred accounts at the top.
  const toggleKeyAccount = async (account, on) => {
    try {
      await updateAccountGoal(account.id, { is_key_account: on });
      refetch();
    } catch (err) { setLookupError(err.response?.data?.error || 'Could not update the key-account flag'); }
  };
  const sortAccounts = (field, direction) => handleSort(field, field === 'is_key_account' && sortField !== 'is_key_account' ? 'desc' : direction);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false); // false | 'stage' | 'primaryOwner' | 'addOwner'

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMore, setShowMore] = useState(false);
  useOpenOnNew(() => setShowCreateModal(true));
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  // Inline editing
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const [industrySearchText, setIndustrySearchText] = useState('');

  // Standard industry list
  const INDUSTRIES = [
    'Aerospace & Defense', 'Agriculture', 'Automotive', 'Banking & Finance',
    'Biotechnology', 'Chemicals', 'Construction', 'Consumer Electronics',
    'Data Centers', 'Education', 'Energy', 'Entertainment',
    'Food & Beverage', 'Government', 'Healthcare', 'Hospitality',
    'Insurance', 'Logistics & Transportation', 'Manufacturing', 'Media',
    'Mining', 'Networking & Telecom', 'Oil & Gas', 'Pharmaceuticals',
    'Real Estate', 'Retail', 'Semiconductors', 'Software & IT',
    'Technology', 'Utilities',
  ];

  // Merge standard list with any custom industries from loaded accounts
  const industryItems = useMemo(() => {
    const customIndustries = accounts
      .map(a => a.industry)
      .filter(Boolean)
      .filter(ind => !INDUSTRIES.includes(ind));
    const all = [...new Set([...INDUSTRIES, ...customIndustries])].sort();
    return all.map(name => ({ id: name, name }));
  }, [accounts]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    address: '',
    country: '',
    state: '',
    stage_id: '',
    lead_source_id: '',
    notes: '',
    owners: [],
  });

  // Load lookup data once
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [stagesRes, sourcesRes, usersRes, ownerTypesRes] = await Promise.all([
          getStages(),
          getLeadSources(),
          getUsers(),
          getOwnershipRoleTypes(),
        ]);
        setStages(stagesRes.data);
        setLeadSources(sourcesRes.data);
        setUsers(usersRes.data.filter(u => u.active));
        setOwnershipRoleTypes(ownerTypesRes.data);
      } catch (err) {
        setLookupError('Failed to load filter options');
      }
    };
    loadLookups();
  }, []);

  const handleCreateAccount = async (e) => {
    e.preventDefault();

    // Check for duplicates
    if (formData.name && !duplicateWarning) {
      const dupCheck = await checkDuplicateAccount(formData.name);
      if (dupCheck.data.exists) {
        setDuplicateWarning(dupCheck.data.account);
        return;
      }
    }

    try {
      const response = await createAccount(formData);
      setShowCreateModal(false);
      setDuplicateWarning(null);
      resetForm();
      navigate(`/accounts/${response.data.id}`);
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to create account');
    }
  };

  const handleBulkUpdate = async (updates) => {
    try {
      await bulkUpdateAccounts(selectedIds, updates);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to update accounts');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await bulkDeleteAccounts(selectedIds, true);
      setSelectedIds([]);
      setShowDeleteConfirm(false);
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to delete accounts');
    }
  };

  const handleBulkSetPrimaryOwner = async (userId) => {
    try {
      await bulkSetPrimaryOwner(selectedIds, userId);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to set primary owner');
    }
  };

  const handleBulkAddOwner = async (userId) => {
    try {
      await bulkAddOwner(selectedIds, userId);
      setSelectedIds([]);
      setShowBulkActions(false);
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to add owner');
    }
  };

  const handleInlineEdit = (account) => {
    setEditingId(account.id);
    setEditData({
      name: account.name,
      industry: account.industry || '',
      stage_id: account.stage_id || '',
      website: account.website || '',
    });
  };

  const handleSaveInline = async () => {
    try {
      await updateAccount(editingId, editData);
      setEditingId(null);
      setEditData({});
      refetch();
    } catch (err) {
      setLookupError(err.response?.data?.error || 'Failed to update account');
    }
  };

  const handleCancelInline = () => {
    setEditingId(null);
    setEditData({});
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === accounts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(accounts.map(a => a.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      website: '',
      address: '',
      country: '',
      state: '',
      stage_id: '',
      lead_source_id: '',
      notes: '',
      owners: [],
    });
    setIndustrySearchText('');
  };

  const addOwner = () => {
    setFormData(prev => ({
      ...prev,
      owners: [...prev.owners, { user_id: '', ownership_role_type_id: '', is_primary: prev.owners.length === 0 }],
    }));
  };

  const updateOwner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      owners: prev.owners.map((o, i) => i === index ? { ...o, [field]: value } : o),
    }));
  };

  const removeOwner = (index) => {
    setFormData(prev => ({
      ...prev,
      owners: prev.owners.filter((_, i) => i !== index),
    }));
  };

  // Extracted renderCell to accept (col, account) for MobileCard compatibility
  const renderCell = (col, account) => {
    const isEditing = editingId === account.id;
    const primaryOwner = account.owners?.find(o => o.is_primary);

    switch (col.key) {
      case 'is_key_account':
        return <KeyStar on={!!account.is_key_account} name={account.name} what="key account" onToggle={canDo('accounts', 'edit') ? (on) => toggleKeyAccount(account, on) : undefined} />;
      case 'name':
        return isEditing ? (
          <input type="text" className="form-input form-input-sm" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
        ) : (
          <Link to={`/accounts/${account.id}`} className="account-name-link">{account.name}</Link>
        );
      case 'stage_name':
        return isEditing ? (
          <select className="form-select form-select-sm" value={editData.stage_id} onChange={(e) => setEditData({ ...editData, stage_id: e.target.value })}>
            <option value="">Select Stage</option>
            {stages.map(s => (<option key={s.id} value={s.id}>{s.name}</option>))}
          </select>
        ) : (
          account.stage_name ? <span className="badge badge-primary">{account.stage_name}</span> : <EmptyValue />
        );
      case 'industry':
        return isEditing ? (
          <input type="text" className="form-input form-input-sm" value={editData.industry} onChange={(e) => setEditData({ ...editData, industry: e.target.value })} />
        ) : (account.industry || <EmptyValue />);
      case 'primary_owner':
        return primaryOwner?.user_name || <EmptyValue />;
      case 'lead_source_name':
        return account.lead_source_name || <EmptyValue />;
      case 'website':
        return account.website ? <a href={account.website} target="_blank" rel="noopener noreferrer">{account.website}</a> : <EmptyValue />;
      case 'country':
        return account.country || <EmptyValue />;
      case 'state':
        return account.state || <EmptyValue />;
      case 'address':
        return account.address || <EmptyValue />;
      case 'created_at':
        return account.created_at ? new Date(account.created_at).toLocaleDateString() : <EmptyValue />;
      default:
        return account[col.key] || <EmptyValue />;
    }
  };

  // Build row actions for an account
  const getRowActions = (account) => {
    const actions = [
      {
        key: 'view',
        label: 'View',
        icon: Eye,
        onClick: () => navigate(`/accounts/${account.id}`),
      },
    ];
    if (canDo('accounts', 'edit')) {
      actions.push({
        key: 'edit',
        label: 'Edit Inline',
        icon: Edit,
        onClick: () => handleInlineEdit(account),
      });
    }
    return actions;
  };

  return (
    <div className="accounts-page">
      <div className="page-header">
        <h1 className="page-title">Accounts</h1>
        <div className="page-actions">
          <SavedViews entityType="accounts" current={{ search, stageFilter, ownerFilter, sortBy: sortField, sortOrder: sortDirection }} onApply={(v) => { setSearch(v.search || ''); setStageFilter(v.stageFilter || []); setOwnerFilter(v.ownerFilter || []); if (v.sortBy) handleSort(v.sortBy, v.sortOrder || 'asc'); }} />
          <ColumnsButton entityType="accounts" />
          <ExportMenu
            onExportExcel={exportAccountsExcel}
            onExportPdf={exportAccountsPdf}
            entityName="Accounts"
          />
          {canDo('accounts', 'create') && (
            <button className="btn btn-secondary" onClick={() => setShowImportModal(true)}>
              <Upload size={18} />
              Import CSV
            </button>
          )}
          {canDo('accounts', 'create') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              New Account
            </button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      {/* Search and Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="filter-bar">
            <div className="search-input">
              <Search size={18} className="search-input-icon" aria-hidden="true" />
              <input
                type="text"
                className="form-input"
                placeholder="Search accounts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search accounts"
              />
            </div>
            <MultiSelectFilter
              label="Stages"
              options={stages}
              selected={stageFilter}
              onChange={setStageFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by stage"
            />
            <MultiSelectFilter
              label="Owners"
              options={users}
              selected={ownerFilter}
              onChange={setOwnerFilter}
              className="filter-dropdown-md"
              ariaLabel="Filter by owner"
            />
            {(search || stageFilter.length > 0 || ownerFilter.length > 0) && (
              <button
                className="btn-clear-filters"
                onClick={() => { setSearch(''); setStageFilter([]); setOwnerFilter([]); }}
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <div className="bulk-actions">
            {canDo('accounts', 'edit') && (
              <>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'stage' ? false : 'stage')}
                  >
                    Change Stage
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'stage' && (
                    <div className="dropdown-menu">
                      {stages.map(s => (
                        <button
                          key={s.id}
                          className="dropdown-item"
                          onClick={() => handleBulkUpdate({ stage_id: s.id })}
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'primaryOwner' ? false : 'primaryOwner')}
                  >
                    Set Primary Owner
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'primaryOwner' && (
                    <div className="dropdown-menu">
                      {users.map(u => (
                        <button
                          key={u.id}
                          className="dropdown-item"
                          onClick={() => handleBulkSetPrimaryOwner(u.id)}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="dropdown">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowBulkActions(showBulkActions === 'addOwner' ? false : 'addOwner')}
                  >
                    Add Owner
                    <ChevronDown size={16} />
                  </button>
                  {showBulkActions === 'addOwner' && (
                    <div className="dropdown-menu">
                      {users.map(u => (
                        <button
                          key={u.id}
                          className="dropdown-item"
                          onClick={() => handleBulkAddOwner(u.id)}
                        >
                          {u.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            {canDo('accounts', 'delete') && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 size={16} />
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {/* Accounts Table / Mobile Cards */}
      <div className="card">
        {isMobile ? (
          /* Mobile Card Layout */
          <div className="mobile-card-list">
            {loading && accounts.length === 0 ? (
              <CardSkeleton count={4} />
            ) : accounts.length === 0 ? (
              search || stageFilter.length > 0 || ownerFilter.length > 0 ? (
                <EmptyStateFiltered
                  onClearFilters={() => { setSearch(''); setStageFilter([]); setOwnerFilter([]); }}
                />
              ) : (
                <EmptyState
                  icon={Building2}
                  title="No accounts yet"
                  description="Add your first account to start tracking your customers and prospects."
                  actionLabel={canDo('accounts', 'create') ? 'New Account' : undefined}
                  onAction={canDo('accounts', 'create') ? () => setShowCreateModal(true) : undefined}
                  actionIcon={Plus}
                />
              )
            ) : (
              accounts.map(account => {
                const isEditing = editingId === account.id;
                return (
                  <MobileCard
                    key={account.id}
                    columns={visibleColumns}
                    data={account}
                    renderCell={renderCell}
                    primaryField="name"
                    selected={selectedIds.includes(account.id)}
                    onSelect={() => toggleSelect(account.id)}
                    actions={
                      isEditing ? (
                        <div style={{ display: 'flex', gap: '4px' }}>
                          <button className="btn btn-icon btn-success" onClick={handleSaveInline} title="Save"><Save size={16} /></button>
                          <button className="btn btn-icon" onClick={handleCancelInline} title="Cancel"><X size={16} /></button>
                        </div>
                      ) : (
                        <RowActions id={account.id} actions={getRowActions(account)} />
                      )
                    }
                  />
                );
              })
            )}
          </div>
        ) : (
          /* Desktop Table Layout */
          <div className="table-container">
            <table className="table" aria-label="Accounts list">
              <thead>
                <tr>
                  <th scope="col" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.length === accounts.length && accounts.length > 0}
                      onChange={toggleSelectAll}
                      aria-label="Select all accounts"
                    />
                  </th>
                  {visibleColumns.map(col => (
                    <SortableHeader key={col.key} label={col.label} field={col.key} sortField={sortField} sortDirection={sortDirection} onSort={sortAccounts} />
                  ))}
                  <th scope="col" className="col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && accounts.length === 0 ? (
                  <TableSkeleton columns={visibleColumns.length} rows={8} hasCheckbox={true} hasActions={true} />
                ) : accounts.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + 2}>
                      {search || stageFilter.length > 0 || ownerFilter.length > 0 ? (
                        <EmptyStateFiltered
                          onClearFilters={() => { setSearch(''); setStageFilter([]); setOwnerFilter([]); }}
                        />
                      ) : (
                        <EmptyState
                          icon={Building2}
                          title="No accounts yet"
                          description="Add your first account to start tracking your customers and prospects."
                          actionLabel={canDo('accounts', 'create') ? 'New Account' : undefined}
                          onAction={canDo('accounts', 'create') ? () => setShowCreateModal(true) : undefined}
                          actionIcon={Plus}
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  accounts.map(account => {
                    const isEditing = editingId === account.id;

                    return (
                      <tr key={account.id}>
                        <td>
                          <input type="checkbox" className="checkbox" checked={selectedIds.includes(account.id)} onChange={() => toggleSelect(account.id)} />
                        </td>
                        {visibleColumns.map(col => (
                          <td key={col.key}>{renderCell(col, account)}</td>
                        ))}
                        <td>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn btn-icon btn-success" onClick={handleSaveInline} title="Save"><Save size={16} /></button>
                              <button className="btn btn-icon" onClick={handleCancelInline} title="Cancel"><X size={16} /></button>
                            </div>
                          ) : (
                            <RowActions id={account.id} actions={getRowActions(account)} />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        {/* Server-side Pagination */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      </div>

      {/* Create Account Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setDuplicateWarning(null);
          resetForm();
        }}
        title="New Account"
        size="lg"
      >
        <form onSubmit={handleCreateAccount}>
          <div className="modal-body">
            {duplicateWarning && (
              <div className="alert alert-warning">
                An account with the name "{duplicateWarning.name}" already exists.
              </div>
            )}

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Account Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setDuplicateWarning(null);
                  }}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Industry</label>
                <TypeaheadSelect
                  id="new-account-industry"
                  placeholder="Search or type industry..."
                  items={industryItems}
                  value={formData.industry}
                  displayValue={industrySearchText}
                  onChange={(val, name) => { setFormData({ ...formData, industry: val }); setIndustrySearchText(name); }}
                  onAddNew={() => {
                    const typed = document.querySelector('#new-account-industry')?.value || '';
                    if (typed.trim()) {
                      setFormData(prev => ({ ...prev, industry: typed.trim() }));
                      setIndustrySearchText(typed.trim());
                    }
                  }}
                  addNewLabel="Use custom industry"
                />
              </div>
            </div>

            {showMore ? (
              <>
            <div className="grid grid-cols-2">
            <div className="form-group">
                <label className="form-label">Website</label>
                <input
                  type="url"
                  className="form-input"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Stage</label>
                <select
                  className="form-select"
                  value={formData.stage_id}
                  onChange={(e) => setFormData({ ...formData, stage_id: e.target.value })}
                >
                  <option value="">Select Stage</option>
                  {stages.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Address</label>
              <input
                type="text"
                className="form-input"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Country</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Lead Source</label>
              <select
                className="form-select"
                value={formData.lead_source_id}
                onChange={(e) => setFormData({ ...formData, lead_source_id: e.target.value })}
              >
                <option value="">Select Lead Source</option>
                {leadSources.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Owners</label>
              {formData.owners.map((owner, index) => (
                <div key={index} className="owner-row">
                  <select
                    className="form-select"
                    value={owner.user_id}
                    onChange={(e) => updateOwner(index, 'user_id', e.target.value)}
                  >
                    <option value="">Select User</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                  <select
                    className="form-select"
                    value={owner.ownership_role_type_id}
                    onChange={(e) => updateOwner(index, 'ownership_role_type_id', e.target.value)}
                  >
                    <option value="">Role Type</option>
                    {ownershipRoleTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                  <label className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={owner.is_primary}
                      onChange={(e) => updateOwner(index, 'is_primary', e.target.checked)}
                    />
                    Primary
                  </label>
                  <button
                    type="button"
                    className="btn btn-icon"
                    onClick={() => removeOwner(index)}
                    aria-label="Remove owner"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-secondary btn-sm" onClick={addOwner}>
                <Plus size={16} />
                Add Owner
              </button>
            </div>

            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>
              </>
            ) : (
              <p className="form-more">
                <button type="button" className="btn-link" onClick={() => setShowMore(true)}>More details…</button>
                <span className="text-muted text-sm"> — everything else can be filled in on the record.</span>
              </p>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowCreateModal(false);
                setDuplicateWarning(null);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Create Account
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleBulkDelete}
        title="Delete Accounts"
        message={`Are you sure you want to delete ${selectedIds.length} account(s)? This will also delete all associated contacts.`}
        confirmText="Delete"
      />

      {/* Import CSV Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={importAccounts}
        entityName="Accounts"
        sampleColumns={['name', 'industry', 'website', 'address', 'country', 'state', 'stage', 'lead_source', 'rep_firm', 'notes', 'linkedin_company_url']}
        sampleData={[
          { name: 'Acme Corporation', industry: 'Manufacturing', website: 'https://acme.com', address: '123 Main St', country: 'USA', state: 'CA', stage: 'Lead', lead_source: 'Website', rep_firm: '', notes: '', linkedin_company_url: '' }
        ]}
        requiredColumns={['name']}
        matchDescription="account name"
        onLoadData={refetch}
      />
    </div>
  );
}

export default Accounts;
